import { Location } from '@angular/common';
import { Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AnalysisApiClient, ApplicationApiClient, isNkHttpError } from '@nk/api-client-lib';
import { GENERIC_AUTH_SERVICE_TOKEN, GenericAuthService } from '@nk/auth-lib';
import {
  getPageTypeFromUrl,
  registerIdentityWithPosthog,
  registerRedirectErrorCatcher,
} from '@nk/core/posthog/posthog';
import { AppService } from '@nk/access-block';
import { AppIdentityCompositeEntity } from '@nk/data-entities-lib';
import { DEFAULT_LANGUAGE_CODE } from '@nk/localization-lib';
import { APP_ENV_CONFIG_TOKEN, AppEnvConfig, filterTruthyValues } from '@nk/utils-lib';
import { Diagram } from 'gojs';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { enGbLocale, nbLocale } from 'ngx-bootstrap/locale';
import posthog from 'posthog-js';
import { catchError, combineLatest, Observable, of, retry } from 'rxjs';
import { first, map, mergeMap } from 'rxjs/operators';

defineLocale('nb', nbLocale);
defineLocale('en', enGbLocale);

function initPosthog(): void {
  posthog.init('phc_1NCSDMuAxGvQS4TmCQKOH6JKzViXaV2JzzpCupr68g9', { api_host: 'https://posthog.enin.co' });
}

@Component({
  selector: 'body',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  public loadingApp = true;
  public translationsReady$: Observable<boolean>;
  private readonly _destroy: DestroyRef = inject(DestroyRef);

  constructor(
    private _appService: AppService,
    private _api: ApplicationApiClient,
    private _analysisApi: AnalysisApiClient,
    private _router: Router,
    private _location: Location,
    private _translate: TranslateService,
    private _bootstrapLocaleService: BsLocaleService,
    @Inject(APP_ENV_CONFIG_TOKEN) private _appEnvConfig: AppEnvConfig,
    @Inject(GENERIC_AUTH_SERVICE_TOKEN) private _authService: GenericAuthService,
  ) {
    Diagram.licenseKey =
      '73f942e0b36428a800ca0d2b113f69ed1bb37b649e831ea75e0545a1ef0e68173089ee2b01818b9381a81aa91a7ac08dd4956c799e48053be132d38951f280ace23173e04409559ea70225c386fa2abef9';
    if (this._appEnvConfig.environmentKey !== 'local') {
      initPosthog();
    }
  }

  public ngOnInit(): void {
    this._router.events.pipe(takeUntilDestroyed(this._destroy)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.loadingApp = false;

        const pageType = getPageTypeFromUrl(event.url);
        posthog.capture('$pageview', { pageType });
      }

      if (event instanceof NavigationError) {
        // Hide loading indicator
      }
    });

    registerRedirectErrorCatcher(this._location);

    this._api
      .get('system-status', {}, null)
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe({
        next: () => {
          console.info('Connected to the Application API');
        },
        error: (error: unknown) => {
          if (isNkHttpError(error)) {
            posthog.capture('$error', {
              endpoint: 'system-status',
              status: error.status,
              message: error.error.message,
            });
            console.error('Could not connect to the Application API', error);
            void this._router.navigate(['/maintenance']);
            if (error.status === 401) {
              // Logout customer and clear slug.
              this._authService.logout('unauthorized');
            }
            if (error.status === 403) {
              this._authService.logout('forbidden');
            }
          } else {
            posthog.capture('$error', {
              endpoint: 'system-status',
              status: 'unknown',
              message: error,
            });
          }
          // Redirect to server is down page.
        },
      });

    this._analysisApi
      .get('system-status', {}, null)
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe({
        next: () => {
          console.info('Connected to the Analysis API');
        },
        error: (error: unknown) => {
          console.error('Could not connect to the Analysis API.', error);
          //   this._router.navigate(['/maintenance']);
          // Redirect to server is down page.
        },
      });
    this._translate.setDefaultLang(DEFAULT_LANGUAGE_CODE);
    this._bootstrapLocaleService.use(DEFAULT_LANGUAGE_CODE);
    document.documentElement.lang = DEFAULT_LANGUAGE_CODE;
    this.translationsReady$ = combineLatest([
      this._translate.getTranslation('en'),
      this._translate.getTranslation('nb'),
    ]).pipe(
      retry(3),
      catchError((error: unknown) => {
        console.error('Could not find translationfile', error);
        throw error;
      }),
      filterTruthyValues(),
      map(() => true),
    );

    this._authService.authInfo$
      .pipe(
        first(),
        mergeMap((authInfo) => {
          if (authInfo.isValid()) {
            return this._appService.identityDataCache.get();
          } else {
            return of(null);
          }
        }),
        takeUntilDestroyed(this._destroy),
      )
      .subscribe((identity: AppIdentityCompositeEntity) => {
        if (identity) {
          if (this._appEnvConfig.environmentKey !== 'local') {
            registerIdentityWithPosthog(identity);
          }
          this._appService.startHeartbeat();

          if (identity.app_user.language_code) {
            this._translate.use(identity.app_user.language_code);
            this._bootstrapLocaleService.use(identity.app_user.language_code);
            document.documentElement.lang = identity.app_user.language_code;
          }
          localStorage.setItem('user_email', identity.app_user.email);
        }
      });
  }
}
